/**
 * ModalEditor
 *
 * Property editor for modal control type
 * Configures modal behavior, appearance, sizing, and interactions
 *
 * SRP: Responsible ONLY for configuring modal properties
 * Control Type: 'modal'
 *
 * Properties Configured (10):
 * 1. modalTitle - Title in modal header
 * 2. width - Modal width (px or %)
 * 3. height - Modal height (px or %)
 * 4. backgroundColor - Modal background color
 * 5. headerBackgroundColor - Header color
 * 6. closeOnBackdropClick - Click outside closes modal
 * 7. showCloseButton - Display X button
 * 8. showHeader - Display header section
 * 9. animationType - Entry animation (fade/slide/scale)
 * 10. zIndex - Stacking order
 */

import React, { useCallback, useMemo } from 'react';
import type { PropertyEditor, PropertyEditorProps } from '../../../registry/PropertyEditor.types';
import type { FormControl } from '@atlas-forms/types-js';
import { TextPropertyEditor } from '../BaseEditors/TextPropertyEditor';
import { ColorPropertyEditor } from '../BaseEditors/ColorPropertyEditor';
import { SelectPropertyEditor } from '../BaseEditors/SelectPropertyEditor';
import { NumberPropertyEditor } from '../BaseEditors/NumberPropertyEditor';
import { CheckboxPropertyEditor } from '../BaseEditors/CheckboxPropertyEditor';

/**
 * Modal configuration
 */
interface ModalConfig {
  modalTitle?: string;
  width?: string;
  height?: string;
  backgroundColor?: string;
  headerBackgroundColor?: string;
  closeOnBackdropClick?: boolean;
  showCloseButton?: boolean;
  showHeader?: boolean;
  animationType?: 'fade' | 'slide' | 'scale';
  zIndex?: number;
}

/**
 * ModalEditor Component
 * Renders property editors for all modal configuration
 */
const ModalEditorComponent: React.FC<PropertyEditorProps> = ({
  control,
  onConfigChange,
}) => {
  const config = useMemo(
    () => (control.config as ModalConfig) || {},
    [control.config],
  );

  const handleModalTitleChange = useCallback(
    (value: string) => onConfigChange('modalTitle', value),
    [onConfigChange],
  );

  const handleWidthChange = useCallback(
    (value: string) => onConfigChange('width', value),
    [onConfigChange],
  );

  const handleHeightChange = useCallback(
    (value: string) => onConfigChange('height', value),
    [onConfigChange],
  );

  const handleBackgroundColorChange = useCallback(
    (value: string) => onConfigChange('backgroundColor', value),
    [onConfigChange],
  );

  const handleHeaderBackgroundColorChange = useCallback(
    (value: string) => onConfigChange('headerBackgroundColor', value),
    [onConfigChange],
  );

  const handleCloseOnBackdropClickChange = useCallback(
    (value: boolean) => onConfigChange('closeOnBackdropClick', value),
    [onConfigChange],
  );

  const handleShowCloseButtonChange = useCallback(
    (value: boolean) => onConfigChange('showCloseButton', value),
    [onConfigChange],
  );

  const handleShowHeaderChange = useCallback(
    (value: boolean) => onConfigChange('showHeader', value),
    [onConfigChange],
  );

  const handleAnimationTypeChange = useCallback(
    (value: string) => onConfigChange('animationType', value as 'fade' | 'slide' | 'scale'),
    [onConfigChange],
  );

  const handleZIndexChange = useCallback(
    (value: number) => onConfigChange('zIndex', value),
    [onConfigChange],
  );

  const animationTypeOptions = useMemo(
    () => [
      { label: 'Fade', value: 'fade' },
      { label: 'Slide', value: 'slide' },
      { label: 'Scale', value: 'scale' },
    ],
    [],
  );

  return (
    <div className="af-property-editor__section modal-editor">
      <h3 className="af-property-editor__section-title">Modal Properties</h3>

      <TextPropertyEditor
        label="Modal Title"
        value={config.modalTitle || ''}
        onChange={handleModalTitleChange}
        placeholder="Enter modal title"
        helpText="Title displayed in modal header"
      />

      <TextPropertyEditor
        label="Width"
        value={config.width || '500px'}
        onChange={handleWidthChange}
        placeholder="e.g., 500px or 80%"
        helpText="Modal width (px or %)"
      />

      <TextPropertyEditor
        label="Height"
        value={config.height || 'auto'}
        onChange={handleHeightChange}
        placeholder="e.g., 400px or auto"
        helpText="Modal height (px or auto)"
      />

      <ColorPropertyEditor
        label="Background Color"
        value={config.backgroundColor || '#FFFFFF'}
        onChange={handleBackgroundColorChange}
        helpText="Modal content background color"
      />

      <ColorPropertyEditor
        label="Header Background Color"
        value={config.headerBackgroundColor || '#F5F5F5'}
        onChange={handleHeaderBackgroundColorChange}
        helpText="Modal header background color"
      />

      <CheckboxPropertyEditor
        label="Close on Backdrop Click"
        value={config.closeOnBackdropClick ?? true}
        onChange={handleCloseOnBackdropClickChange}
        helpText="Modal closes when clicking outside"
      />

      <CheckboxPropertyEditor
        label="Show Close Button"
        value={config.showCloseButton ?? true}
        onChange={handleShowCloseButtonChange}
        helpText="Display X button to close modal"
      />

      <CheckboxPropertyEditor
        label="Show Header"
        value={config.showHeader ?? true}
        onChange={handleShowHeaderChange}
        helpText="Display modal header section"
      />

      <SelectPropertyEditor
        label="Animation Type"
        value={config.animationType || 'fade'}
        onChange={handleAnimationTypeChange}
        options={animationTypeOptions}
        helpText="Modal entry animation style"
      />

      <NumberPropertyEditor
        label="Z-Index"
        value={config.zIndex || 1000}
        onChange={handleZIndexChange}
        min={1}
        max={9999}
        step={1}
        helpText="Stacking order above other elements"
      />
    </div>
  );
};

ModalEditorComponent.displayName = 'ModalEditor';

/**
 * Modal Property Editor Registration
 */
export const ModalEditor: PropertyEditor = {
  type: 'modal',
  label: 'Modal',
  description: 'Configure modal behavior, appearance, sizing, and interactions',
  version: '1.0.0',
  render: (props) => <ModalEditorComponent {...props} />,
};

export default ModalEditor;
